import * as THREE from 'three';

export default class CheckpointRing {
    constructor(scene, position, index, total) {
        this.scene = scene;
        this.position = position;
        this.index = index;
        this.total = total;
        this.collected = false;
        this.radius = 6;
        this.detectionRadius = 15; // increased from 10 for more forgiving detection
        this.mesh = null;
        this.glowMesh = null;
        this.arrowMesh = null;
        this.time = 0;

        this.create();
    }

    create() {
        this.group = new THREE.Group();
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(this.position.x, this.position.z) : 0;
        this.group.position.set(this.position.x, terrainY, this.position.z);

        // Main ring
        const ringGeometry = new THREE.TorusGeometry(this.radius, 0.4, 16, 32);
        const ringMaterial = new THREE.MeshStandardMaterial({
            color: 0x00FFAA,
            emissive: 0x00FF88,
            emissiveIntensity: 0.6,
            metalness: 0.8,
            roughness: 0.2,
            transparent: true,
            opacity: 0.9
        });

        this.mesh = new THREE.Mesh(ringGeometry, ringMaterial);
        this.mesh.rotation.x = Math.PI / 2;
        this.mesh.position.y = 4;
        this.group.add(this.mesh);

        // Inner glow ring
        const glowGeometry = new THREE.TorusGeometry(this.radius - 0.5, 0.15, 8, 32);
        const glowMaterial = new THREE.MeshBasicMaterial({
            color: 0xFFFFFF,
            transparent: true,
            opacity: 0.4
        });
        this.glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
        this.glowMesh.rotation.x = Math.PI / 2;
        this.glowMesh.position.y = 4;
        this.group.add(this.glowMesh);

        // Number indicator floating above
        const numberGeometry = new THREE.SphereGeometry(1.2, 16, 16);
        const numberMaterial = new THREE.MeshStandardMaterial({
            color: 0xFFDD00,
            emissive: 0xFFAA00,
            emissiveIntensity: 0.5,
            metalness: 0.5,
            roughness: 0.3
        });
        const numberSphere = new THREE.Mesh(numberGeometry, numberMaterial);
        numberSphere.position.y = 10;
        this.group.add(numberSphere);

        // Ground detection circle — shows the player where to drive
        const groundRingGeometry = new THREE.RingGeometry(this.detectionRadius - 1, this.detectionRadius, 32);
        const groundRingMaterial = new THREE.MeshBasicMaterial({
            color: 0x00FFAA,
            transparent: true,
            opacity: 0.35,
            side: THREE.DoubleSide,
            depthWrite: false
        });
        this.groundRing = new THREE.Mesh(groundRingGeometry, groundRingMaterial);
        this.groundRing.rotation.x = -Math.PI / 2;
        this.groundRing.position.y = 0.05;
        this.group.add(this.groundRing);

        // Filled ground disc for visibility
        const groundDiscGeometry = new THREE.CircleGeometry(this.detectionRadius, 32);
        const groundDiscMaterial = new THREE.MeshBasicMaterial({
            color: 0x00FFAA,
            transparent: true,
            opacity: 0.1,
            side: THREE.DoubleSide,
            depthWrite: false
        });
        this.groundDisc = new THREE.Mesh(groundDiscGeometry, groundDiscMaterial);
        this.groundDisc.rotation.x = -Math.PI / 2;
        this.groundDisc.position.y = 0.04;
        this.group.add(this.groundDisc);

        // Minimap marker
        const markerGeometry = new THREE.CircleGeometry(4, 8);
        const markerMaterial = new THREE.MeshBasicMaterial({
            color: 0x00FFAA,
            side: THREE.DoubleSide
        });
        this.markerMesh = new THREE.Mesh(markerGeometry, markerMaterial);
        this.markerMesh.rotation.x = -Math.PI / 2;
        const markerTerrainY = heightMap ? heightMap.getHeight(this.position.x, this.position.z) : 0;
        this.markerMesh.position.set(this.position.x, markerTerrainY + 5, this.position.z);
        this.markerMesh.layers.set(1);
        this.scene.add(this.markerMesh);

        this.scene.add(this.group);
    }

    update(deltaTime) {
        if (this.collected || !this.mesh) return;

        this.time += deltaTime;

        // Gentle rotation
        this.mesh.rotation.z += deltaTime * 1.5;
        this.glowMesh.rotation.z -= deltaTime * 0.8;

        // Float up and down
        const floatOffset = Math.sin(this.time * 2) * 0.5;
        this.mesh.position.y = 4 + floatOffset;
        this.glowMesh.position.y = 4 + floatOffset;

        // Pulse glow
        const pulseIntensity = 0.4 + Math.sin(this.time * 3) * 0.2;
        this.glowMesh.material.opacity = pulseIntensity;

        // Pulse ground ring
        if (this.groundRing) {
            this.groundRing.material.opacity = 0.25 + Math.sin(this.time * 2) * 0.15;
        }
    }

    checkBusProximity(bus) {
        if (this.collected || !bus || !bus.mesh) return false;

        const busPos = bus.mesh.position;
        const dx = busPos.x - this.position.x;
        const dz = busPos.z - this.position.z;
        const distance = Math.sqrt(dx * dx + dz * dz);

        // Debug: log proximity every ~60 frames when within 50 units
        if (distance < 50 && (!this._debugTimer || this._debugTimer++ % 60 === 0)) {
            this._debugTimer = 1;
            console.log(`[Checkpoint ${this.index}] bus(${busPos.x.toFixed(1)}, ${busPos.z.toFixed(1)}) ring(${this.position.x.toFixed(1)}, ${this.position.z.toFixed(1)}) dist=${distance.toFixed(1)} detect=${this.detectionRadius}`);
        }

        return distance < this.detectionRadius;
    }

    collect() {
        if (this.collected) return;
        this.collected = true;

        // Quick scale-up and fade animation
        const startScale = this.group.scale.x;
        const startTime = performance.now();
        const duration = 400;

        const animate = () => {
            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const scale = startScale + progress * 2;
            this.group.scale.set(scale, scale, scale);

            this.mesh.material.opacity = 1 - progress;
            this.glowMesh.material.opacity = (0.4 - progress * 0.4);

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                this.remove();
            }
        };

        animate();

        // Hide minimap marker
        if (this.markerMesh) {
            this.markerMesh.visible = false;
        }
    }

    remove() {
        if (this.group) {
            this.scene.remove(this.group);
        }
        if (this.markerMesh) {
            this.scene.remove(this.markerMesh);
        }
    }

    dispose() {
        this.remove();

        // Dispose Three.js geometries and materials to prevent memory leaks
        if (this.group) {
            this.group.traverse((child) => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(m => m.dispose());
                    } else {
                        child.material.dispose();
                    }
                }
            });
        }
        if (this.markerMesh) {
            if (this.markerMesh.geometry) this.markerMesh.geometry.dispose();
            if (this.markerMesh.material) this.markerMesh.material.dispose();
        }
    }
}
